import { customElement, resolve, type IDisposable, IEventAggregator } from 'aurelia';
import { I18N, Signals } from '@aurelia/i18n';
import { route } from '@aurelia/router';
import template from './settings.html';
import { getUserSettings, upsertUserSetting } from '../services/api.service';
import { DATE_FORMAT_OPTIONS, getDateFormat, setDateFormat, type DateFormatOption } from '../services/date-format.service';
import { LANGUAGES } from '../services/languages';
import { getAgeAutoThresholdYears, setAgeAutoThresholdYears } from '../services/person-age-settings.service';
import { getDefaultVisibility, setDefaultVisibility } from '../services/default-visibility.service';
import { getShowFamilyMedia, setShowFamilyMedia } from '../services/media-scope.service';
import type { MediaVisibility } from '../services/api.types';
import {
  DEFAULT_FACE_MAX_WORKING_DIMENSION,
  DEFAULT_FACE_SCORE_THRESHOLD,
  getFaceMarginBottomPercent,
  getFaceMarginSidePercent,
  getFaceMarginTopPercent,
  getFaceMaxWorkingDimension,
  getFaceScoreThreshold,
  getFaceZoomThresholdPx,
  MAX_FACE_MAX_WORKING_DIMENSION,
  MAX_FACE_SCORE_THRESHOLD,
  MIN_FACE_MAX_WORKING_DIMENSION,
  MIN_FACE_SCORE_THRESHOLD,
  setFaceMarginBottomPercent,
  setFaceMarginSidePercent,
  setFaceMarginTopPercent,
  setFaceMaxWorkingDimension,
  setFaceScoreThreshold,
  setFaceZoomThresholdPx,
} from '../services/face-detection-settings.service';

export const THEME_CHANGED_CHANNEL = 'radish:theme:changed';

type SettingsTab = 'general' | 'faceRecognition';

@route({ path: 'settings', title: 'Settings' })
@customElement({ name: 'settings-view', template })
export class SettingsView {
  private readonly i18n: I18N = resolve(I18N);
  private readonly ea: IEventAggregator = resolve(IEventAggregator);
  private _themeSub: IDisposable | null = null;
  private _localeSub: IDisposable | null = null;

  public activeTab: SettingsTab = 'general';
  public theme: 'light' | 'dark' = 'light';
  public language = 'en';
  public dateFormat: DateFormatOption = 'locale';
  public ageAutoThresholdYears = 3;
  public defaultUploadVisibility: MediaVisibility = 'private';
  public showFamilyMedia = true;
  public faceMarginTopPercent = 40;
  public faceMarginSidePercent = 15;
  public faceMarginBottomPercent = 15;
  public faceZoomThresholdPx = 80;
  public faceScoreThreshold = DEFAULT_FACE_SCORE_THRESHOLD;
  public faceMaxWorkingDimension = DEFAULT_FACE_MAX_WORKING_DIMENSION;
  public showLangDropdown = false;
  public readonly dateFormatOptions = DATE_FORMAT_OPTIONS;
  public readonly languages = LANGUAGES;

  public get currentLanguage() {
    return this.languages.find(language => language.value === this.language) ?? this.languages[0];
  }

  public selectTab(tab: SettingsTab): void {
    this.activeTab = tab;
  }

  public binding(): void {
    this._themeSub = this.ea.subscribe(THEME_CHANGED_CHANNEL, (theme: 'light' | 'dark') => {
      this.theme = theme;
    });
    this._localeSub = this.ea.subscribe(Signals.I18N_EA_CHANNEL, () => {
      this.language = this.i18n.getLocale();
    });
  }

  public unbinding(): void {
    this._themeSub?.dispose();
    this._localeSub?.dispose();
  }

  public async loading(): Promise<void> {
    this.theme = (document.documentElement.getAttribute('data-theme') as 'light' | 'dark') ?? 'light';
    this.language = this.i18n.getLocale();
    this.dateFormat = getDateFormat();
    this.ageAutoThresholdYears = getAgeAutoThresholdYears();
    this.defaultUploadVisibility = getDefaultVisibility();
    this.showFamilyMedia = getShowFamilyMedia();
    this.faceMarginTopPercent = getFaceMarginTopPercent();
    this.faceMarginSidePercent = getFaceMarginSidePercent();
    this.faceMarginBottomPercent = getFaceMarginBottomPercent();
    this.faceZoomThresholdPx = getFaceZoomThresholdPx();
    this.faceScoreThreshold = getFaceScoreThreshold();
    this.faceMaxWorkingDimension = getFaceMaxWorkingDimension();

    try {
      const settings = await getUserSettings();
      for (const setting of settings) {
        if (setting.key === 'theme') this.applyTheme(setting.value as 'light' | 'dark', false);
        if (setting.key === 'language') this.applyLanguage(setting.value, false);
        if (setting.key === 'dateFormat') this.applyDateFormat(setting.value as DateFormatOption);
        if (setting.key === 'personAgeAutoThresholdYears') {
          const years = Number(setting.value);
          if (Number.isFinite(years)) {
            setAgeAutoThresholdYears(years);
            this.ageAutoThresholdYears = years;
          }
        }
        if (setting.key === 'defaultUploadVisibility') {
          const value = setting.value === 'family' ? 'family' : 'private';
          setDefaultVisibility(value);
          this.defaultUploadVisibility = value;
        }
        if (setting.key === 'showFamilyMedia') {
          const value = setting.value !== 'false';
          setShowFamilyMedia(value);
          this.showFamilyMedia = value;
        }
        if (setting.key === 'faceMarginTopPercent') {
          const value = Number(setting.value);
          if (Number.isFinite(value)) {
            setFaceMarginTopPercent(value);
            this.faceMarginTopPercent = value;
          }
        }
        if (setting.key === 'faceMarginSidePercent') {
          const value = Number(setting.value);
          if (Number.isFinite(value)) {
            setFaceMarginSidePercent(value);
            this.faceMarginSidePercent = value;
          }
        }
        if (setting.key === 'faceMarginBottomPercent') {
          const value = Number(setting.value);
          if (Number.isFinite(value)) {
            setFaceMarginBottomPercent(value);
            this.faceMarginBottomPercent = value;
          }
        }
        if (setting.key === 'faceZoomThresholdPx') {
          const value = Number(setting.value);
          if (Number.isFinite(value)) {
            setFaceZoomThresholdPx(value);
            this.faceZoomThresholdPx = value;
          }
        }
        if (setting.key === 'faceScoreThreshold') {
          const value = Number(setting.value);
          if (Number.isFinite(value)) {
            setFaceScoreThreshold(value);
            this.faceScoreThreshold = value;
          }
        }
        if (setting.key === 'faceMaxWorkingDimension') {
          const value = Number(setting.value);
          if (Number.isFinite(value)) {
            setFaceMaxWorkingDimension(value);
            this.faceMaxWorkingDimension = value;
          }
        }
      }
    } catch {
      // ignore
    }
  }

  public applyTheme(theme: 'light' | 'dark', save: boolean): void {
    this.theme = theme;
    document.documentElement.setAttribute('data-theme', theme);
    if (save) localStorage.setItem('radish-theme', theme);
    this.ea.publish(THEME_CHANGED_CHANNEL, theme);
    if (save) void upsertUserSetting('theme', theme).catch(() => {});
  }

  public applyLanguage(language: string, save: boolean): void {
    this.language = language;
    void this.i18n.setLocale(language);
    if (save) {
      localStorage.setItem('radish-language', language);
      void upsertUserSetting('language', language).catch(() => {});
    }
  }

  public changeDateFormat(dateFormat: DateFormatOption): void {
    this.applyDateFormat(dateFormat);
    void upsertUserSetting('dateFormat', dateFormat).catch(() => {});
  }

  public applyDateFormat(dateFormat: DateFormatOption): void {
    this.dateFormat = dateFormat;
    setDateFormat(dateFormat);
  }

  public changeAgeAutoThreshold(years: number | string): void {
    const parsed = Number(years);
    const value = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    this.ageAutoThresholdYears = value;
    setAgeAutoThresholdYears(value);
    void upsertUserSetting('personAgeAutoThresholdYears', String(value)).catch(() => {});
  }

  public changeDefaultUploadVisibility(visibility: MediaVisibility): void {
    this.defaultUploadVisibility = visibility;
    setDefaultVisibility(visibility);
    void upsertUserSetting('defaultUploadVisibility', visibility).catch(() => {});
  }

  public changeShowFamilyMedia(showFamilyMedia: boolean | string): void {
    const value = showFamilyMedia === true || showFamilyMedia === 'true';
    this.showFamilyMedia = value;
    setShowFamilyMedia(value);
    void upsertUserSetting('showFamilyMedia', String(value)).catch(() => {});
  }

  public changeFaceMarginTopPercent(value: number | string): void {
    const parsed = Number(value);
    const clamped = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    this.faceMarginTopPercent = clamped;
    setFaceMarginTopPercent(clamped);
    void upsertUserSetting('faceMarginTopPercent', String(clamped)).catch(() => {});
  }

  public changeFaceMarginSidePercent(value: number | string): void {
    const parsed = Number(value);
    const clamped = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    this.faceMarginSidePercent = clamped;
    setFaceMarginSidePercent(clamped);
    void upsertUserSetting('faceMarginSidePercent', String(clamped)).catch(() => {});
  }

  public changeFaceMarginBottomPercent(value: number | string): void {
    const parsed = Number(value);
    const clamped = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    this.faceMarginBottomPercent = clamped;
    setFaceMarginBottomPercent(clamped);
    void upsertUserSetting('faceMarginBottomPercent', String(clamped)).catch(() => {});
  }

  public changeFaceZoomThresholdPx(value: number | string): void {
    const parsed = Number(value);
    const clamped = Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 1;
    this.faceZoomThresholdPx = clamped;
    setFaceZoomThresholdPx(clamped);
    void upsertUserSetting('faceZoomThresholdPx', String(clamped)).catch(() => {});
  }

  public changeFaceScoreThreshold(value: number | string): void {
    const parsed = Number(value);
    const clamped = Number.isFinite(parsed)
      ? Math.min(Math.max(parsed, MIN_FACE_SCORE_THRESHOLD), MAX_FACE_SCORE_THRESHOLD)
      : DEFAULT_FACE_SCORE_THRESHOLD;
    this.faceScoreThreshold = clamped;
    setFaceScoreThreshold(clamped);
    void upsertUserSetting('faceScoreThreshold', String(clamped)).catch(() => {});
  }

  public changeFaceMaxWorkingDimension(value: number | string): void {
    const parsed = Number(value);
    const clamped = Number.isFinite(parsed)
      ? Math.round(Math.min(Math.max(parsed, MIN_FACE_MAX_WORKING_DIMENSION), MAX_FACE_MAX_WORKING_DIMENSION))
      : DEFAULT_FACE_MAX_WORKING_DIMENSION;
    this.faceMaxWorkingDimension = clamped;
    setFaceMaxWorkingDimension(clamped);
    void upsertUserSetting('faceMaxWorkingDimension', String(clamped)).catch(() => {});
  }

  public selectLanguage(language: string): void {
    this.showLangDropdown = false;
    this.applyLanguage(language, true);
  }
}
